import moment, { Moment } from "moment";
import "moment-timezone";
import { RegexUUIDExp } from "./constants";
import { persistor } from "../redux/store";

// Types
interface RuleItem {
  value: string;
  label: string;
  details: any;
  clazz: string;
}

interface RuleGroup {
  title: {
    value: string;
  };
  rule: RuleItem[];
}

interface RuleData {
  type: string;
  name: string;
  configurationDescriptor: {
    nodeDefinition: any;
  };
  clazz: string;
}

// Moment dates format
export const formatDate = (
  date: string | number | Date | Moment | null,
  dateFormat?: string,
  timezone?: string,
): string => {
  if (!date) return "";

  let parsedDate: Moment;

  if (moment.isMoment(date)) {
    parsedDate = date;
  } else if (typeof date === "number") {
    parsedDate = moment.utc(date);
  } else if (date instanceof Date) {
    parsedDate = moment.utc(date);
  } else if (typeof date === "string") {
    // Attempt to parse string as ISO
    // If it looks like an ISO string but lacks 'Z' or '+HH:mm', treat as UTC (API standard)
    if (
      date.includes("T") &&
      !date.includes("Z") &&
      !/[+-]\d{2}(:?\d{2})?$/.test(date)
    ) {
      parsedDate = moment.utc(date);
    } else {
      parsedDate = moment.parseZone(date);
    }

    if (!parsedDate.isValid()) {
      // fallback to native parsing (with warning)
      parsedDate = moment(new Date(date));
    }
  } else {
    return ""; // or handle error
  }

  if (timezone) {
    // convert to provided timezone before formatting
    try {
      parsedDate = parsedDate.tz
        ? parsedDate.tz(timezone)
        : moment(parsedDate).tz(timezone);
    } catch (e) {
      // if tz not available or fails, fall back to original parsedDate
    }
  }

  return dateFormat ? parsedDate.format(dateFormat) : parsedDate.format();
};

export const formatTime = (value: number): string => {
  const hours = Math.floor(value / 60);
  const minutes = value % 60;
  return `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}`;
};

export const getTimeStamp = (newTime: string, newDate: Date): number => {
  const [hours, minutes] = newTime.split(":").map(Number);

  const istMoment = moment
    .tz(newDate, "Asia/Kolkata")
    .set({ hour: hours, minute: minutes, second: 0, millisecond: 0 });

  return istMoment.valueOf();
};

// Remove Demo1_ prefix from meter serial numbers
export const formatMeterSerialNumber = (
  serialNumber: string | undefined | null,
): string => {
  if (!serialNumber) return "";
  const prefix = "Demo1_";
  if (serialNumber.startsWith(prefix)) {
    return serialNumber.slice(prefix.length);
  }
  return serialNumber;
};

// First letter uppercase
export const firstLetterUppercase = (value: string): string => {
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

// Get Page Header Title
export const getPageTitle = (path: string): string | null => {
  switch (path) {
    case "/meter-dashboard":
      return "Meter 360";
    case "/consumer-360":
      return "Customer Management 360";
    case "/consumer-dashboard":
      return "Consumer 360";
    case "/dashboard-360":
      return "Dashboard 360";
    case "/notification-settings":
      return "Notification & Alert Settings";
    case "/read-cluster":
      return "Read Cluster";
    case "/billing-determinant":
      return "Billing Determinants";
    case "/wallet-balance":
      return "Wallet Balance";
    case "/register-rules":
      return "Register Rules";
    case "/register-rule-book":
      return "Register Rule Book";
    case "/meters":
      return "Meters";
    case "/consumer":
      return "Consumer";
    case "/reports":
      return "Reports";
    case "/energy-audit-loss-report":
      return "Energy Audit/Loss Report";
    case "/payment-processing-dashboard":
      return "Payment Processing Dashboard";
    case "/bill-generation-system":
      return "Bill Generation System";
    case "/tariff-form":
      return "Tariff Form";
    case "/tariffs":
      return "Tariffs";
    case "/new-tariffs":
      return "New Tariffs (v2)";
    case "/payments-v2":
      return "New Payments (v2)";
    case "/billing-v2":
      return "New Bills (v2)";
    case "/adjustments-v2":
      return "New Adjustments (v2)";
    case "/new-tariffs/form":
      return "New Tariffs Form";
    case "/rate-management-dashboard":
      return "Rate Management Dashboard";
    case "/collections-management-dashboard":
      return "Collections Management Dashboard";
    case "/commitment":
      return "Commitment Contract";
    case "/location":
      return "Location";
    case "/site":
      return "Site";
    case "/workorders":
      return "Work Orders";
    case "/events":
      return "Events";
    case "/meter-type":
      return "Meter Type";
    case "/vee-rule-builder":
      return "VEE Rule Builder";
    case "/billing-rule-builder":
      return "Billing Rule Builder";
    case "/prosumer-dashboard":
      return "Prosumer Dashboard";
    case "/prosumer-scheduling":
      return "Prosumer Scheduling";
    case "/consumer-registration":
      return "Consumer Registration";
    case "/service-provider":
      return "Service Provider";
    case "/real-time-dashboard":
      return "Real Time Dashboard";
    case "/energy-block-dashboard":
      return "Energy Block Dashboard";
    case "/billing-determinant-report":
      return "Billing Determinant Report";
    case "/monthly-consumption-report":
      return "Monthly Consumption Report";
    case "/load-profile-report":
      return "Load Profile Report";
    case "/daily-data-collection-report":
      return "Daily Data Collection Report";
    case "/meter-event-report":
      return "Meter Event Report";
    case "/vee-exceptions-report":
      return "VEE Exceptions Report";
    case "/billing-report":
      return "Billing Report";
    case "/saidi-report":
      return "SAIDI Report";
    case "/saifi-report":
      return "SAIFI Report";
    case "/caidi-report":
      return "CAIDI Report";
    case "/caifi-report":
      return "CAIFI Report";
    case "/missing-reads-dashboard":
      return "Missing Reads Dashboard";
    case "/energy-performance-report":
      return "Energy Performance Report";
    case "/basic-reports":
      return "Meter Data Reports";
    case "/basic-reports-dashboard":
      return "Basic Reports Dashboard";
    case "/report-analytics/dashboard":
      return "Report & Analytics — Dashboard";
    case "/report-analytics/timeseries":
      return "Report & Analytics — Consumption";
    case "/report-analytics/anomalies":
      return "Report & Analytics — Anomaly Detection";
    case "/report-analytics/load-profile":
      return "Report & Analytics — Load Profile";
    case "/report-analytics/forecast":
      return "Report & Analytics — Forecast";
    case "/report-analytics/voltage":
      return "Report & Analytics — Voltage Quality";
    case "/report-analytics/frequency":
      return "Report & Analytics — Frequency";
    case "/report-analytics/demand":
      return "Report & Analytics — Demand Peaks";
    case "/report-analytics/events":
      return "Report & Analytics — Event Log";
    case "/report-analytics/compare":
      return "Report & Analytics — Compare Meters";
    case "/report-analytics/uptime":
      return "Report & Analytics — Uptime";
    case "/report-analytics/solar":
      return "Report & Analytics — Solar";
    default:
      return null;
  }
};

// | AEI0000000010 | e3423840-e909-11ef-a6a1-c5603cdacbcd   | MD Transcon |
// | AEI0000000009 | ce702c10-e909-11ef-a6a1-c5603cdacbcd   | MD Transcon |
// | AEI0000000008 | c581f2a0-e909-11ef-a6a1-c5603cdacbcd   | MD Transcon |
// | AEI0000000007 | bb63ebc0-e909-11ef-a6a1-c5603cdacbcd   | MD Transcon |
// | S1D00629      | 929f0fd0-e909-11ef-a6a1-c5603cdacbcd   | MD Transcon |
// | S1D00319      | 6fc147d0-e909-11ef-a6a1-c5603cdacbcd   | MD Transcon |
// | SLS-27011     | 3d5fca00-e909-11ef-a6a1-c5603cdacbcd   | SLS         |
// | SLS-4020221   | f7655c40-e908-11ef-a6a1-c5603cdacbcd   | SLS         |

export const isUUID = (uid: string | number): RegExpMatchArray | null => {
  let s = "" + uid;

  return s.match(RegexUUIDExp);
};

export const resetStore = (): void => {
  persistor.purge();
  localStorage.clear();
  window.location.href = "/login";
};

export function toTitleCase(str: string | undefined): string {
  return (
    str?.replace(
      /\w\S*/g,
      (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    ) || ""
  );
}

function getRandomColor(): string {
  return "#" + Math.floor(Math.random() * 16777215).toString(16);
}

export const ruleOfDataFormater = (
  constData: RuleGroup[],
  dataString: string | any[],
): RuleGroup[] => {
  const JSONDATA: RuleData[] =
    typeof dataString === "string" ? JSON.parse(dataString) : dataString;

  console.log(JSONDATA);
  const updatedRules: RuleGroup[] = constData.map((group) => ({
    ...group,
    rule: [] as RuleItem[],
  }));

  JSONDATA?.forEach((obj) => {
    const matchingGroup = updatedRules.find(
      (group) => group.title.value === obj.type,
    );

    if (matchingGroup) {
      matchingGroup.rule.push({
        value: obj.name,
        label: toTitleCase(obj.name),
        details: obj.configurationDescriptor.nodeDefinition,
        clazz: obj.clazz,
      });
    }
  });

  return updatedRules;
};

export function removeNullKeys<T = any>(obj: T): T {
  if (Array.isArray(obj)) {
    return obj.map(removeNullKeys) as unknown as T;
  } else if (obj !== null && typeof obj === "object") {
    const newObj: any = {};
    for (const key in obj) {
      const value = (obj as any)[key];
      if (value !== null) {
        newObj[key] = typeof value === "object" ? removeNullKeys(value) : value;
      }
    }
    return newObj;
  }
  return obj;
}

export function replaceNullWithEmptyString<T = any>(obj: T): T {
  if (Array.isArray(obj)) {
    return obj.map(replaceNullWithEmptyString) as unknown as T;
  } else if (obj !== null && typeof obj === "object") {
    const newObj: any = {};
    for (const key in obj) {
      const value = (obj as any)[key];
      newObj[key] =
        value === null
          ? ""
          : typeof value === "object"
            ? replaceNullWithEmptyString(value)
            : value;
    }
    return newObj;
  }
  return obj;
}
